// ============================================================
// QuamSpace · /square/workspace
//
// QuamSpace is the OPERATOR workspace surface — drafts, notes,
// research, and personal creation memory. It mounts the shared
// QuamSpaceCanvas in `quamspace` variant. Below the canvas a
// "Saved Work" rail lists the operator's existing personal
// sq_workspaces (live via useWorkspaces).
//
// QuamSpace is intentionally distinct from Content Studio:
//   QuamSpace = operator workspace / memory / drafts (scope='personal')
//   Studio    = per-project creation / runs / outputs (scope='project')
//
// Build mode in QuamSpace renders the same generation lane but the
// Site Builder handoff seam is intentionally disabled here, because
// Site Builder requires a project context. The canvas surfaces this
// honestly via a disabled "Save as Site Draft (project required)"
// button. To use Build → Site Builder, open Studio inside a project.
// ============================================================

import { useMemo, useState } from 'react'
import EmptyState from '../../components/EmptyState'
import AuthRequiredState from '../../components/AuthRequiredState'
import QuamSpaceCanvas from '../../components/QuamSpaceCanvas/QuamSpaceCanvas'
import { SHELL_TOKENS, text, timeAgo } from '../../shell/styles'
import { recordShellAction } from '../../observability/sentry'
import { useWorkspaces } from '../../hooks/useWorkspaces'
import { api } from '../../api/client'

const SAVED_VIEWS: { id: string; label: string }[] = [
  { id: 'everything', label: 'Everything' },
  { id: 'pinned',     label: 'Pinned' },
  { id: 'document',   label: 'Document' },
  { id: 'notes',      label: 'Notes' },
  { id: 'build',      label: 'Build' },
  { id: 'pdf',        label: 'PDF' },
]

export default function QuamspaceIndex() {
  const [view, setView] = useState('everything')
  const [creating, setCreating] = useState(false)

  const filters = useMemo(
    () => ({
      scope: 'personal' as const,
      pinned: view === 'pinned' ? true : undefined,
      current_mode:
        view !== 'everything' && view !== 'pinned' ? view : undefined,
      limit: 50,
    }),
    [view],
  )

  const { workspaces, loading, error, authRequired, refresh, live } = useWorkspaces(filters)

  async function createWorkspace() {
    if (!live || creating) return
    setCreating(true)
    try {
      await api.workspace.create({
        name: `QuamSpace ${workspaces.length + 1}`,
        scope: 'personal',
        mode: view === 'notes' ? 'notes' : 'document',
      })
      await refresh()
      recordShellAction('quamspace.workspace_created', { view })
    } finally {
      setCreating(false)
    }
  }

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Operator framing header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 18,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              ...text.kicker,
              fontSize: 10,
              letterSpacing: '0.28em',
              color: SHELL_TOKENS.inkFaint,
              marginBottom: 6,
            }}
          >
            OPERATOR · QUAMSPACE
          </div>
          <div style={text.editorial}>QuamSpace</div>
          <div
            style={{
              fontSize: 11,
              color: SHELL_TOKENS.inkMuted,
              marginTop: 6,
              fontFamily: 'var(--font-mono)',
              maxWidth: 620,
              lineHeight: 1.6,
            }}
          >
            Operator workspace · drafts, notes, research, and personal creation memory.
            Saved into your private sq_workspaces with scope='personal'. Build mode here is
            generation only — Site Builder publishing lives inside a project Studio.
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            recordShellAction('quamspace.new_workspace_intent', { view })
            void createWorkspace()
          }}
          disabled={!live || creating}
          style={{
            padding: '10px 16px',
            borderRadius: 10,
            border: `1px solid ${SHELL_TOKENS.brandLine}`,
            background: SHELL_TOKENS.brandSoft,
            color: SHELL_TOKENS.brand,
            cursor: !live || creating ? 'not-allowed' : 'pointer',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
            opacity: !live || creating ? 0.55 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {creating ? 'Saving…' : '+ Save Empty Workspace'}
        </button>
      </div>

      {/* Creation canvas */}
      <div style={{ marginBottom: 26 }}>
        <QuamSpaceCanvas variant="quamspace" initialMode="document" />
      </div>

      {/* Saved Work rail */}
      <div
        style={{
          ...text.kicker,
          fontSize: 9,
          letterSpacing: '0.24em',
          color: SHELL_TOKENS.inkWhisper,
          marginBottom: 10,
        }}
      >
        SAVED WORK · OPERATOR MEMORY
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
        {SAVED_VIEWS.map((v) => {
          const active = view === v.id
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: active
                  ? `1px solid ${SHELL_TOKENS.brandLine}`
                  : `1px solid ${SHELL_TOKENS.border}`,
                background: active ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
                color: active ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: active ? 700 : 500,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {v.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <EmptyState title="Loading saved work" body="Reading sq_workspaces (scope='personal')." />
      ) : authRequired ? (
        <AuthRequiredState
          title="Sign in to open QuamSpace"
          body="QuamSpace syncs your private saved work, drafts, and build memory against your authenticated browser session. Sign in to load saved work and keep workspace actions attached to your account."
        />
      ) : error ? (
        <EmptyState title="Saved work unavailable" body={error} />
      ) : workspaces.length === 0 ? (
        <EmptyState
          title="No saved work yet"
          body={
            live
              ? "Use the canvas above to draft, then save into your QuamSpace memory."
              : 'QuamSpace persistence is only enabled in live mode.'
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {workspaces.map((workspace) => (
            <div
              key={workspace.id}
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                  {workspace.name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: SHELL_TOKENS.inkMuted,
                    fontFamily: 'var(--font-mono)',
                    marginTop: 4,
                  }}
                >
                  mode {workspace.current_mode} · {workspace.status}
                  {workspace.pinned ? ' · pinned' : ''}
                </div>
                {workspace.prompt && (
                  <div
                    style={{
                      fontSize: 11,
                      color: SHELL_TOKENS.inkDim,
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {workspace.prompt}
                  </div>
                )}
              </div>

              <div
                style={{
                  fontSize: 9,
                  color: SHELL_TOKENS.inkWhisper,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  whiteSpace: 'nowrap',
                }}
              >
                {timeAgo(workspace.updated_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
